import { Box } from '@nimbus-ds/components';
import { Stack } from 'commons/components';
import { useStorage } from 'commons/hooks';
import { useGetUsageReportQuery } from 'domains/Ai/aiApi';
import AiUsageReport from '../AiUsageReport';
import CardStatisticsDaily from '../CardStatisticsDaily';

const MIN_MINUTES_SAVED_TO_SHOW_REPORT = 300; //5h

interface StoreStatsProps {
  showDailyStats?: boolean;
}

function StoreStats({ showDailyStats = true }: Readonly<StoreStatsProps>) {
  const { data: usageReport } = useGetUsageReportQuery();

  const [showAmounts, setShowAmount] = useStorage(
    'showAmountsDashboard',
    true,
    true,
  );

  function handleOnClick() {
    setShowAmount(!showAmounts);
  }

  if (usageReport === undefined) {
    return null;
  }

  // Only show the AI usage report if the time saved is greater than a min threshold, either this month or the previous one.
  // This is because the first few days of the new month would not meet the threshold and we don't want the report to go away if the previous month was good.
  const showAiUsageReport =
    usageReport.lastMonth.minutesSaved >= MIN_MINUTES_SAVED_TO_SHOW_REPORT ||
    usageReport.currentMonth.minutesSaved >= MIN_MINUTES_SAVED_TO_SHOW_REPORT;

  const reports: React.ReactNode[] = [];

  if (showDailyStats) {
    reports.push(
      <CardStatisticsDaily
        onHideAmounts={handleOnClick}
        showAmounts={showAmounts}
        showInline={!showAiUsageReport}
      />,
    );
  }

  if (showAiUsageReport) {
    reports.push(
      <AiUsageReport
        minutesSaved={usageReport.currentMonth.minutesSaved}
        moneySavedValue={usageReport.currentMonth.moneySavedValue}
        moneySavedCurrency={usageReport.currentMonth.moneySavedCurrency}
      />,
    );
  }

  return (
    <Stack column align="stretch">
      <Box
        display={{
          lg: !showAiUsageReport ? 'flex' : 'grid',
        }}
        gridTemplateColumns={
          showDailyStats && showAiUsageReport ? '2fr 1fr' : '1fr 1fr'
        }
        gridGap="4"
      >
        {reports[0]}
        {reports[1]}
      </Box>
    </Stack>
  );
}

export default StoreStats;
